type StatusMessages = Record<number, string>;

type ResolvedFormError = {
  fields: Record<string, string>;
  form: string | null;
};

function getHttpErrorStatus(error: unknown): number | null {
  let status: number | null = null;
  if (typeof error === 'object' && error !== null) {
    const record = error as { status?: unknown; statusCode?: unknown };
    if (typeof record.status === 'number') {
      status = record.status;
    } else if (typeof record.statusCode === 'number') {
      status = record.statusCode;
    }
  }
  return status;
}

function getHttpErrorCode(error: unknown): string | null {
  let code: string | null = null;
  if (typeof error === 'object' && error !== null) {
    const record = error as { body?: unknown };
    if (typeof record.body === 'object' && record.body !== null) {
      const body = record.body as { code?: unknown };
      if (typeof body.code === 'string') {
        code = body.code;
      }
    }
  }
  return code;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  let record = false;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    record = true;
  }
  return record;
}

function getErrorBody(error: unknown): Record<string, unknown> | null {
  let body: Record<string, unknown> | null = null;
  if (isRecord(error)) {
    if (isRecord(error.body)) {
      body = error.body;
    } else if (typeof error.message === 'string') {
      body = error;
    }
  }
  return body;
}

function getHttpErrorMessage(error: unknown): string | null {
  let message: string | null = null;
  const body = getErrorBody(error);
  if (body && typeof body.message === 'string' && body.message.length > 0) {
    message = body.message;
  }
  return message;
}

function resolveFormError(error: unknown, options?: { fallback?: string }): ResolvedFormError {
  let fallback = 'Something went wrong. Please try again.';
  if (options?.fallback) {
    fallback = options.fallback;
  }
  let form: string | null = fallback;
  const message = getHttpErrorMessage(error);
  if (message) {
    form = message;
  }
  return {
    fields: {},
    form: form,
  };
}

function createErrorMessageGetter(fallback: string, statusMessages: StatusMessages = {}) {
  return (error: unknown): string => {
    let message = fallback;
    const bodyMessage = getHttpErrorMessage(error);
    if (bodyMessage) {
      message = bodyMessage;
    } else {
      const status = getHttpErrorStatus(error);
      if (status !== null && status in statusMessages) {
        message = statusMessages[status];
      }
    }
    return message;
  };
}

function createResponseErrorMessageGetter(fallback: string, statusMessages: StatusMessages = {}) {
  return (response: { status: number; body: unknown }): string => {
    let message = fallback;
    const bodyMessage = getHttpErrorMessage({ status: response.status, body: response.body });
    if (bodyMessage) {
      message = bodyMessage;
    } else if (response.status in statusMessages) {
      message = statusMessages[response.status];
    }
    return message;
  };
}

export { getHttpErrorStatus };
export { getHttpErrorCode };
export { getHttpErrorMessage };
export { resolveFormError };
export { createErrorMessageGetter };
export { createResponseErrorMessageGetter };
export type { ResolvedFormError };
